import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@/context/ThemeContext';

const NODE_W = 200;
const NODE_H = 52;
const NODE_RX = 8;

const NODES = [
  { id: 'foundation', x: 290, y: 10, label: 'Foundation', sub: 'Three-Phase Basics', route: '/foundation', live: true },
  { id: 'transformers', x: 40, y: 115, label: 'Transformer Systems', sub: 'Delta-Wye, %Z', route: '/transformers', live: true },
  { id: 'per-unit', x: 290, y: 115, label: 'Per-Unit Analysis', sub: 'Normalization', route: '/per-unit', live: true },
  { id: 'symmetrical-components', x: 290, y: 220, label: 'Sym. Components', sub: 'Pos/Neg/Zero Sequence', route: '/symmetrical-components', live: true },
  { id: 'sequence-networks', x: 290, y: 325, label: 'Sequence Networks', sub: 'Zero-Seq Models', route: '/sequence-networks', live: true },
  { id: 'fault-analysis', x: 290, y: 430, label: 'Fault Analysis', sub: 'SLG, LL, DLG, 3-Phase', route: '/fault-analysis', live: false },
  { id: 'protection', x: 40, y: 430, label: 'Protection & Grounding', sub: 'Coordination, TCC', route: '/protection', live: false },
  { id: 'distribution-design', x: 550, y: 292, label: 'Distribution Design', sub: 'Feeder Design, SAIDI', route: '/distribution-design', live: false },
];

const EDGES = [
  { d: 'M 360 62 Q 225 90 140 115' },
  { d: 'M 390 62 L 390 115' },
  { d: 'M 490 36 C 565 36 565 318 550 318' },
  { d: 'M 240 141 L 290 141' },
  { d: 'M 240 141 C 375 141 375 318 550 318' },
  { d: 'M 390 167 L 390 220' },
  { d: 'M 390 272 L 390 325' },
  { d: 'M 390 377 L 390 430' },
  { d: 'M 290 456 L 240 456' },
  { d: 'M 240 456 C 375 456 375 318 550 318' },
];

const TOOLTIPS = {
  foundation: { desc: 'Start here. AC phasors, three-phase power, and the per-phase equivalent circuit.', prereq: 'None — entry point' },
  transformers: { desc: 'Delta-wye connections, vector groups, leakage reactance, and nameplate impedance.', prereq: 'Foundation' },
  'per-unit': { desc: 'Normalize all voltages and impedances to a common base. The language of power systems fault analysis.', prereq: 'Foundation + Transformer Systems' },
  'symmetrical-components': { desc: "Fortescue's theorem: decompose any unbalanced fault into positive, negative, and zero-sequence sets.", prereq: 'Per-Unit Analysis' },
  'sequence-networks': { desc: 'Build Thevenin equivalent positive, negative, and zero-sequence network models at the fault point.', prereq: 'Symmetrical Components' },
  'fault-analysis': { desc: 'Calculate SLG, LL, DLG, and three-phase fault currents using connected sequence networks.', prereq: 'Sequence Networks' },
  protection: { desc: 'System grounding, overcurrent relay settings, time-current coordination, and CTI verification.', prereq: 'Fault Analysis' },
  'distribution-design': { desc: 'Feeder topology, voltage drop, conductor sizing, capacitor banks, and SAIDI/SAIFI reliability.', prereq: 'All Modules (M01–M07)' },
};

const ConceptMap = () => {
  const navigate = useNavigate();
  const { dark } = useTheme();
  const [hovered, setHovered] = useState(null);
  const [tooltip, setTooltip] = useState({ visible: false, id: null, x: 0, y: 0 });

  const nodeFill = (node) => {
    if (node.live) return dark ? '#0d4438' : '#ccfbf1';
    if (hovered === node.id) return dark ? '#1e293b' : '#f8fafc';
    return dark ? '#0f172a' : '#ffffff';
  };

  const nodeStroke = (node) => {
    if (node.live) return '#0d9488';
    if (hovered === node.id) return '#14b8a6';
    return dark ? '#334155' : '#cbd5e1';
  };

  const labelColor = (node) => {
    if (node.live) return dark ? '#5eead4' : '#0f766e';
    return dark ? '#e2e8f0' : '#1e293b';
  };

  const handleEnter = useCallback((node, e) => {
    setHovered(node.id);
    setTooltip({ visible: true, id: node.id, x: e.clientX, y: e.clientY });
  }, []);

  const handleMove = useCallback((e) => {
    if (tooltip.visible) {
      setTooltip(prev => ({ ...prev, x: e.clientX, y: e.clientY }));
    }
  }, [tooltip.visible]);

  const handleLeave = useCallback(() => {
    setHovered(null);
    setTooltip({ visible: false, id: null, x: 0, y: 0 });
  }, []);

  const edgeColor = dark ? '#334155' : '#cbd5e1';
  const arrowFill = dark ? '#475569' : '#94a3b8';

  return (
    <div className="relative w-full" onMouseMove={handleMove}>
      <svg
        viewBox="0 0 780 510"
        className="w-full"
        style={{ maxHeight: '520px' }}
        aria-label="Power Systems concept map"
      >
        <defs>
          <marker id="arr" markerWidth="8" markerHeight="6" refX="7" refY="3" orient="auto">
            <polygon points="0 0, 8 3, 0 6" fill={arrowFill} />
          </marker>
        </defs>

        {EDGES.map((edge, i) => (
          <path
            key={i}
            d={edge.d}
            fill="none"
            stroke={edgeColor}
            strokeWidth="1.8"
            markerEnd="url(#arr)"
          />
        ))}

        {NODES.map(node => (
          <g
            key={node.id}
            data-testid={`concept-node-${node.id}`}
            role="button"
            tabIndex={0}
            aria-label={`Navigate to ${node.label}`}
            onClick={() => navigate(node.route)}
            onKeyDown={(e) => e.key === 'Enter' && navigate(node.route)}
            onMouseEnter={(e) => handleEnter(node, e)}
            onMouseLeave={handleLeave}
            style={{ cursor: 'pointer' }}
          >
            <rect
              x={node.x}
              y={node.y}
              width={NODE_W}
              height={NODE_H}
              rx={NODE_RX}
              fill={nodeFill(node)}
              stroke={nodeStroke(node)}
              strokeWidth={node.live ? 2 : 1.5}
              style={{ transition: 'fill 0.15s ease, stroke 0.15s ease' }}
            />
            <text
              x={node.x + NODE_W / 2}
              y={node.y + 21}
              textAnchor="middle"
              fontSize="13"
              fontWeight="600"
              fontFamily="IBM Plex Sans, sans-serif"
              fill={labelColor(node)}
            >
              {node.label}
            </text>
            <text
              x={node.x + NODE_W / 2}
              y={node.y + 37}
              textAnchor="middle"
              fontSize="10"
              fontFamily="Work Sans, sans-serif"
              fill={dark ? '#64748b' : '#94a3b8'}
            >
              {node.sub}
            </text>
            {node.live ? (
              <g>
                <rect x={node.x + NODE_W - 38} y={node.y - 9} width={32} height={14} rx={7} fill="#0d9488" />
                <text x={node.x + NODE_W - 22} y={node.y + 1} textAnchor="middle" fontSize="7.5" fill="white" fontWeight="700" fontFamily="IBM Plex Sans">LIVE</text>
              </g>
            ) : (
              <g>
                <rect x={node.x + NODE_W - 47} y={node.y - 9} width={41} height={14} rx={7} fill={dark ? '#1e293b' : '#f1f5f9'} />
                <text x={node.x + NODE_W - 26} y={node.y + 1} textAnchor="middle" fontSize="7" fill={dark ? '#64748b' : '#94a3b8'} fontWeight="600" fontFamily="IBM Plex Sans">SOON</text>
              </g>
            )}
          </g>
        ))}
      </svg>

      {tooltip.visible && tooltip.id && (
        <div
          style={{
            position: 'fixed',
            left: tooltip.x + 14,
            top: tooltip.y - 8,
            zIndex: 9999,
            pointerEvents: 'none',
            maxWidth: '260px',
          }}
          className="bg-slate-900 text-white text-xs p-3.5 rounded-lg shadow-2xl border border-slate-700"
        >
          <p className="font-semibold text-teal-400 mb-1.5">
            {NODES.find(n => n.id === tooltip.id)?.label}
          </p>
          <p className="text-slate-300 mb-1.5 leading-relaxed">
            {TOOLTIPS[tooltip.id]?.desc}
          </p>
          <p className="text-slate-500 text-[10px]">
            <span className="text-slate-400 font-medium">Prerequisites: </span>
            {TOOLTIPS[tooltip.id]?.prereq}
          </p>
        </div>
      )}
    </div>
  );
};

export default ConceptMap;
